import React, { useState } from 'react';
import { Phone, MessageCircle, Mail, ArrowRight, Sparkles, MapPin, CreditCard } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';
import GoogleReviews from '../components/GoogleReviews';
import { CONTACT_EMAIL, CLUBS } from '../data/mock';
import { trackLead } from '../lib/track';

const INTEREST_OPTIONS = [
  "Séance d'essai Hyrox",
  'Séance découverte CrossFit (débutant)',
  "Séance d'essai CrossFit (confirmé)",
  'Drop-in CrossFit / Hyrox',
];

// Intégrations Deciplus — mêmes tunnels que ponts-jumeaux-crossfit.com
const DECIPLUS = {
  inscription: 'https://member-app.deciplus.pro/fenuafitnessgroup',
  essaiHyrox: 'https://member-app.deciplus.pro/fenuafitnessgroup/tunnel?customMenu=Essai%20Hyrox',
  decouverteCrossfit: 'https://member-app.deciplus.pro/fenuafitnessgroup/tunnel?customMenu=D%C3%A9couverte%20CrossFit',
  initiations: 'https://member-app.deciplus.pro/fenuafitnessgroup/tunnel?customMenu=R%C3%A9server%20ses%20initiations',
};

const WA_NUMBER = '33788193740'; // WhatsApp Hybrid : 07 88 19 37 40
const WA_BASE = `https://wa.me/${WA_NUMBER}`;

const WA_OPTIONS = [
  {
    key: 'hyrox',
    label: 'Essai Hyrox',
    desc: "Je veux essayer un cours Hyrox",
    msg: "Bonjour, je souhaiterais faire un cours d'essai d'Hyrox dès que possible, voici mes informations : Nom : ... Prénom : ... Date de naissance : ... Mail : ... Numéro de tel : ... Salle souhaitée (Toulouse / Colomiers) : ...",
  },
  {
    key: 'course',
    label: 'Je prépare une course',
    desc: 'Objectif Hyrox : je veux un plan',
    msg: "Bonjour, je prépare une course Hyrox et je souhaiterais des informations sur la préparation chez Hybrid, voici mes informations : Nom : ... Prénom : ... Date de la course : ... Mail : ... Numéro de tel : ...",
  },
  {
    key: 'crossfit',
    label: 'Abonné CrossFit',
    desc: 'Mon accès Hyrox est offert',
    msg: "Bonjour, je suis abonné CrossFit (Ponts-Jumeaux / Colomiers) et je souhaiterais activer mon accès Hyrox offert, voici mes informations : Nom : ... Prénom : ... Mail : ... Numéro de tel : ...",
  },
];

const DROPIN = {
  stripeUrl: 'https://buy.stripe.com/aEU8yBaP27tS8LecNS',
  price: '20 €',
  waMsg: "Hello, je viens de payer mon drop-in (20 €) et je souhaiterais confirmer ma présence. Je pratique à : ... Je viendrai le : ... à ...h Salle (Toulouse / Colomiers) : ... Mon prénom / nom : ...",
  mailSubject: 'Drop-in payé : confirmation de présence',
  mailBody:
    "Hello,\nJe viens de payer mon drop-in (20 €).\nJe pratique à : ...\nJe viendrai le : ... à ...h\nSalle : Toulouse ou Colomiers\nMon prénom / nom : ...\nÀ très vite !",
};

const INSTAGRAM = 'https://www.instagram.com/hybrid_training.club/';

const baseField =
  'w-full bg-[#131313] border border-[#262626] rounded-md px-4 py-3 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-accent transition-colors';

function ContactForm() {
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', tel: '', message: '', website: '' });
  const [interests, setInterests] = useState([]);
  const [status, setStatus] = useState(null); // null | 'sending' | 'ok' | 'error'
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (label) =>
    setInterests((cur) => (cur.includes(label) ? cur.filter((i) => i !== label) : [...cur, label]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, interests }),
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok && out.ok) {
        setStatus('ok');
        setForm({ prenom: '', nom: '', email: '', tel: '', message: '', website: '' });
        setInterests([]);
        trackLead('reserver', interests);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'ok') {
    return (
      <div className="mt-5 flex flex-1 flex-col items-start justify-center gap-2 rounded-xl border border-accent/40 bg-[#161616] p-6">
        <p className="font-display text-xl text-accent">Merci !</p>
        <p className="text-sm text-[var(--hx-muted)]">Ta demande est envoyée. On te recontacte très vite pour caler ton créneau.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 flex flex-1 flex-col gap-3">
      <fieldset className="flex flex-col gap-2">
        <legend className="font-cond text-[10px] tracking-[0.25em] text-[var(--hx-muted)] mb-1">CE QUI T'INTÉRESSE</legend>
        {INTEREST_OPTIONS.map((label) => (
          <label key={label} className="flex cursor-pointer items-center gap-2.5 rounded-md border border-[#262626] px-4 py-2.5 text-sm text-[var(--hx-text)] transition hover:border-accent">
            <input type="checkbox" checked={interests.includes(label)} onChange={() => toggleInterest(label)} className="h-4 w-4 shrink-0 accent-accent" style={{ accentColor: 'var(--hx-accent)' }} />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="grid grid-cols-2 gap-3">
        <input type="text" required placeholder="Prénom" value={form.prenom} onChange={set('prenom')} className={baseField} />
        <input type="text" required placeholder="Nom" value={form.nom} onChange={set('nom')} className={baseField} />
      </div>
      <input type="email" required placeholder="Email" value={form.email} onChange={set('email')} className={baseField} />
      <input type="tel" placeholder="Téléphone" value={form.tel} onChange={set('tel')} className={baseField} />
      <textarea rows={3} placeholder="Ton message (objectif, niveau, questions...)" value={form.message} onChange={set('message')} className={`${baseField} flex-1`} />
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} className="hidden" aria-hidden="true" />
      <button type="submit" disabled={status === 'sending'} className="hx-btn justify-center text-sm disabled:opacity-60" data-cta="reserver-form-email">
        {status === 'sending' ? 'Envoi en cours…' : <>Envoyer ma demande <ArrowRight size={15} /></>}
      </button>
      {status === 'error' && (
        <p className="text-center text-[11px] text-accent">L'envoi a échoué. Réessaie ou écris-nous sur WhatsApp.</p>
      )}
    </form>
  );
}

export default function Reserver() {
  return (
    <div className="pt-28 pb-4">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12">
        <Reveal>
          <p className="font-cond text-xs tracking-[0.3em] text-accent flex items-center gap-2">
            <Sparkles size={14} /> DERNIÈRE ÉTAPE
          </p>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">
            Réserve ta séance<br />
            <span className="text-accent">d'essai offerte</span>
          </h1>
          <p className="text-[var(--hx-muted)] mt-5 max-w-xl leading-relaxed">
            Première séance offerte, à Toulouse ou Colomiers. Réserve en ligne en 2 minutes,
            ou contacte-nous directement.
          </p>
        </Reveal>
      </section>

      {/* 1. Réservation Deciplus (canal principal) — Hyrox en avant */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border-2 border-accent/50 bg-[#111] p-8 md:p-12">
            <div className="absolute -right-24 -top-32 h-72 w-72 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                <p className="font-cond text-xs tracking-[0.3em] text-accent">RÉSERVATION EN LIGNE · LE PLUS DIRECT</p>
                <h2 className="font-display text-2xl md:text-3xl mt-3">Inscris-toi directement via notre logiciel interne</h2>
                <p className="mt-3 text-sm leading-relaxed text-[var(--hx-muted)]">
                  Crée ton compte en 2 minutes, choisis ton créneau Hyrox et c'est réservé :
                  tu reçois la confirmation immédiatement.
                </p>
              </div>
              <div className="flex w-full max-w-md flex-col gap-4">
                <a href={DECIPLUS.essaiHyrox} target="_blank" rel="noopener" className="hx-btn w-full justify-center text-base" data-cta="deciplus-essai-hyrox">
                  Réserver mon essai Hyrox <ArrowRight size={17} />
                </a>
                <a
                  href={DECIPLUS.inscription}
                  target="_blank"
                  rel="noopener"
                  data-cta="deciplus-inscription"
                  className="w-full text-center font-cond text-sm tracking-wider px-4 py-3 rounded-md border border-[#333] text-white hover:border-accent hover:text-accent transition-colors"
                >
                  Crée ton compte et inscris-toi ↗
                </a>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <a href={DECIPLUS.decouverteCrossfit} target="_blank" rel="noopener" data-cta="deciplus-decouverte-crossfit" className="rounded-xl border border-[#262626] px-4 py-3 text-center transition hover:border-accent">
                    <span className="block text-sm font-bold text-white">Découverte CrossFit</span>
                    <span className="block text-[11px] text-[var(--hx-muted)]">Chez Ponts-Jumeaux ou Colomiers</span>
                  </a>
                  <a href={DECIPLUS.initiations} target="_blank" rel="noopener" data-cta="deciplus-initiations" className="rounded-xl border border-[#262626] px-4 py-3 text-center transition hover:border-accent">
                    <span className="block text-sm font-bold text-white">Mes initiations</span>
                    <span className="block text-[11px] text-[var(--hx-muted)]">Le parcours débutant</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 2. Contact direct : appel / WhatsApp / formulaire */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12">
        <Reveal>
          <p className="font-cond text-xs tracking-[0.3em] text-accent mb-6">TU PRÉFÈRES NOUS PARLER ?</p>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal className="rounded-2xl border border-[#262626] bg-[#111] p-7 flex flex-col">
            <p className="font-cond text-[11px] tracking-[0.25em] text-accent flex items-center gap-2">
              <Phone size={13} /> LE PLUS RAPIDE
            </p>
            <h2 className="font-display text-2xl mt-3">Appelle-nous</h2>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[var(--hx-muted)]">
              Un coach te répond et cale ton créneau directement, dans la salle de ton choix.
            </p>
            <div className="mt-5 flex flex-col gap-2.5">
              <a href={`tel:+33${CLUBS.toulouse.phone.replace(/\s/g, '').slice(1)}`} className="hx-btn justify-center text-sm" data-cta="reserver-appel-toulouse">
                📞 Toulouse · {CLUBS.toulouse.phone}
              </a>
              <a
                href={`tel:+33${CLUBS.colomiers.phone.replace(/\s/g, '').slice(1)}`}
                data-cta="reserver-appel-colomiers"
                className="text-center font-cond text-sm tracking-wider px-4 py-3 rounded-md border border-[#333] text-white hover:border-accent hover:text-accent transition-colors"
              >
                📞 Colomiers · {CLUBS.colomiers.phone}
              </a>
            </div>
          </Reveal>

          <Reveal className="rounded-2xl border-2 border-accent/40 bg-[#111] p-7 flex flex-col" delay={90}>
            <p className="font-cond text-[11px] tracking-[0.25em] text-accent flex items-center gap-2">
              <MessageCircle size={13} /> LE PLUS SIMPLE
            </p>
            <h2 className="font-display text-2xl mt-3">Écris-nous sur WhatsApp</h2>
            <p className="mt-3 text-sm leading-relaxed text-[var(--hx-muted)]">
              Choisis ta situation, le message est déjà prêt : complète tes infos et envoie.
            </p>
            <div className="mt-5 flex flex-col gap-2.5">
              {WA_OPTIONS.map((o) => (
                <a
                  key={o.key}
                  href={`${WA_BASE}?text=${encodeURIComponent(o.msg)}`}
                  target="_blank"
                  data-cta={`reserver-whatsapp-${o.key}`}
                  rel="noopener"
                  className="group flex items-center justify-between gap-3 rounded-xl border border-[#262626] px-4 py-3.5 transition hover:border-accent hover:bg-[#161616]"
                >
                  <span>
                    <span className="block text-sm font-bold text-white">{o.label}</span>
                    <span className="block text-xs text-[var(--hx-muted)]">{o.desc}</span>
                  </span>
                  <span className="text-accent transition-transform group-hover:translate-x-1" aria-hidden="true">→</span>
                </a>
              ))}
            </div>
          </Reveal>

          <Reveal className="rounded-2xl border border-[#262626] bg-[#111] p-7 flex flex-col" delay={180}>
            <p className="font-cond text-[11px] tracking-[0.25em] text-accent flex items-center gap-2">
              <Mail size={13} /> PAR ÉCRIT
            </p>
            <h2 className="font-display text-2xl mt-3">Le formulaire classique</h2>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      {/* 3. Drop-in */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-[#262626] bg-[#111] p-8 md:p-12">
            <div className="absolute -right-24 -top-32 h-72 w-72 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                <p className="font-cond text-xs tracking-[0.3em] text-accent flex items-center gap-2">
                  <CreditCard size={13} /> DROP-IN
                </p>
                <h2 className="font-display text-2xl md:text-3xl mt-3">Tu es de passage à Toulouse ?</h2>
                <p className="mt-3 text-sm leading-relaxed text-[var(--hx-muted)]">
                  Fais ton drop-in chez nous : paye ta séance en ligne ({DROPIN.price}), puis confirme ta
                  présence en un clic. On te garde une place sur le prochain créneau Hyrox.
                </p>
              </div>
              <div className="flex max-w-md flex-1 flex-col gap-5">
                <div>
                  <p className="font-cond text-[10px] tracking-[0.25em] text-[var(--hx-muted)] mb-2.5">ÉTAPE 1 · LE PAIEMENT</p>
                  <a href={DROPIN.stripeUrl} target="_blank" rel="noopener" className="hx-btn w-full justify-center text-sm" data-cta="dropin-stripe">
                    💳 Payer mon drop-in · {DROPIN.price}
                  </a>
                </div>
                <div>
                  <p className="font-cond text-[10px] tracking-[0.25em] text-[var(--hx-muted)] mb-2.5">ÉTAPE 2 · CONFIRME TA PRÉSENCE EN 1 CLIC</p>
                  <div className="flex flex-col gap-2.5 sm:flex-row">
                    <a href={`${WA_BASE}?text=${encodeURIComponent(DROPIN.waMsg)}`} target="_blank" rel="noopener" data-cta="dropin-whatsapp" className="flex-1 text-center font-cond text-sm tracking-wider px-4 py-3 rounded-md border border-accent/60 text-accent hover:bg-accent hover:text-black transition-colors">
                      WhatsApp
                    </a>
                    <a
                      href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(DROPIN.mailSubject)}&body=${encodeURIComponent(DROPIN.mailBody)}`}
                      className="flex-1 text-center font-cond text-sm tracking-wider px-4 py-3 rounded-md border border-[#333] text-white hover:border-accent hover:text-accent transition-colors"
                    >
                      Mail
                    </a>
                    <a href={INSTAGRAM} target="_blank" rel="noopener" data-cta="dropin-instagram" className="flex-1 text-center font-cond text-sm tracking-wider px-4 py-3 rounded-md border border-[#333] text-white hover:border-accent hover:text-accent transition-colors">
                      Instagram
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 4. Adresses des 2 salles */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-8">
        <Reveal>
          <p className="font-cond text-xs tracking-[0.3em] text-accent mb-6">OÙ NOUS TROUVER ?</p>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {[CLUBS.toulouse, CLUBS.colomiers].map((club, i) => (
            <Reveal key={club.slug} delay={i * 90}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`Hybrid Training Club ${club.address}`)}`}
                target="_blank"
                rel="noopener"
                className="group flex items-start gap-4 rounded-2xl border border-[#262626] bg-[#111] p-6 transition hover:border-accent"
              >
                <MapPin size={20} className="text-accent shrink-0 mt-1" />
                <span>
                  <span className="block font-display text-xl">{club.name}</span>
                  <span className="block text-sm text-[var(--hx-muted)] mt-1">{club.address}</span>
                  <span className="block text-sm text-accent mt-2 font-cond tracking-wider">
                    Itinéraire <ArrowRight size={13} className="inline transition-transform group-hover:translate-x-1" />
                  </span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 5. Avis pour rassurer au moment de la décision */}
      <GoogleReviews />
    </div>
  );
}
